/**
 * PATH: packages/pricelist/src/PricelistToolbar.tsx
 * FILE_ID: PKG-PRICELIST-TOOLBAR-001
 * PURPOSE: The editable action row for the pricelist workbook (New section,
 *          Add column, Columns manager, Import, Export, Row, Save). Extracted
 *          from PricelistWorkbook to keep that file under the 300-line cap.
 * WHY: Pure presentational button row — all behaviour is injected as callbacks
 *      so the parent owns dialog/grid state.
 * RELATED: PricelistWorkbook.tsx (parent), ColumnManagerDialog, ColumnDialog
 */
import { Download, Plus, Save, Upload, Columns3, Loader2, FolderPlus, SlidersHorizontal } from 'lucide-react'
import { useIsPhone } from '@quotech/ui'

export interface PricelistToolbarProps {
  syntheticView: boolean
  canAddColumn: boolean
  /** Columns manager availability (defaults to canAddColumn). The uniform org
   *  schema makes the manager valid on the "All sections" overview too. */
  canManageColumns?: boolean
  activeEditable: boolean
  hasActiveSection: boolean
  saving: boolean
  onNewSection: () => void
  onAddColumn: () => void
  onManageColumns: () => void
  onImport: () => void
  onExport: () => void
  onAddRow: () => void
  onSave: () => void
}

export default function PricelistToolbar({
  syntheticView,
  canAddColumn,
  canManageColumns,
  activeEditable,
  hasActiveSection,
  saving,
  onNewSection,
  onAddColumn,
  onManageColumns,
  onImport,
  onExport,
  onAddRow,
  onSave,
}: PricelistToolbarProps) {
  const isPhone = useIsPhone()
  return (
    <div
      className={
        isPhone
          ? 'qb-pl-toolbar qb-pl-toolbar--phone flex flex-wrap items-center gap-2'
          : 'qb-pl-toolbar flex flex-wrap items-center gap-2'
      }
    >
      <button type="button" onClick={onNewSection} className="qb-pl-btn">
        <FolderPlus className="h-4 w-4" /> New section
      </button>
      <button
        type="button"
        onClick={onAddColumn}
        disabled={!canAddColumn}
        title={syntheticView ? 'Save once to organise items into a section, then add columns' : undefined}
        className="qb-pl-btn"
      >
        <Columns3 className="h-4 w-4" /> Add column
      </button>
      <button
        type="button"
        onClick={onManageColumns}
        disabled={!(canManageColumns ?? canAddColumn)}
        title="Reorder, edit or remove columns (applies to all sections)"
        className="qb-pl-btn"
      >
        <SlidersHorizontal className="h-4 w-4" /> Columns
      </button>
      <button type="button" onClick={onImport} disabled={!activeEditable} className="qb-pl-btn">
        <Upload className="h-4 w-4" /> Import
      </button>
      <button type="button" onClick={onExport} disabled={!hasActiveSection} className="qb-pl-btn">
        <Download className="h-4 w-4" /> Export
      </button>
      <button type="button" onClick={onAddRow} disabled={!activeEditable} className="qb-pl-btn">
        <Plus className="h-4 w-4" /> Row
      </button>
      <button type="button" onClick={onSave} disabled={saving || !activeEditable} className="qb-pl-btn-primary">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
      </button>
    </div>
  )
}
